import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

const PageContainer = styled.div`
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled.h1`
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.thead`
  background-color: ${({ theme }) => theme.colors.background};
`;

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const TableHeaderCell = styled.th`
  text-align: left;
  padding: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

const TableCell = styled.td`
  padding: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        
        // Query all users with role 'customer' or 'user'
        const customersQuery = query(collection(db, 'users'));
        const querySnapshot = await getDocs(customersQuery);
        
        const customersList = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(user => user.role === 'customer' || user.role === 'user');
        
        setCustomers(customersList);
      } catch (err) {
        console.error('Error fetching customers:', err);
        setError('Failed to load customers');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCustomers();
  }, []);

  return (
    <PageContainer>
      <Title>Customer Management</Title>
      <Subtitle>View and manage customer accounts.</Subtitle>
      
      {loading && <LoadingState>Loading customers...</LoadingState>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      {!loading && !error && customers.length === 0 ? (
        <EmptyState>
          <h3>No customers found</h3>
          <p>There are no registered customers in the system.</p>
        </EmptyState>
      ) : (
        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Joined</TableHeaderCell>
                <TableHeaderCell>Partner</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {customers.map(customer => (
                <TableRow key={customer.id}>
                  <TableCell>{customer.displayName || 'N/A'}</TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>
                    {customer.createdAt 
                      ? new Date(customer.createdAt.toDate()).toLocaleDateString() 
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{customer.partnerId || 'None'}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
};

export default CustomerManagement; 